import * as React from 'react';
import styled from '@emotion/styled';
import OpenedSvg from '../images/opened';
import config from '../../../config';
import Link from '../link';

/**
 * Styled collapse button with proper alignment
 * Chevron icon inherits color from parent via currentColor
 */
const CollapseButton = styled.button`
  background: transparent;
  border: none;
  outline: none;
  padding: 0;
  margin-left: auto;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  transition: transform var(--transition-fast, 0.15s ease);

  svg {
    color: inherit;
  }

  &:hover {
    opacity: 0.8;
  }
`;

const StyledMenuLink = styled(Link)`
&[data-level="1"] {
  border: 1px solid transparent !important;
  border-radius: 6px !important;
  padding-right: var(--gap-3x) !important;

  &::before {
    content: none !important;
  }

  &:hover {
    border-color: var(--color-border) !important;
  }

  &.menu-item-active {
    background-color: var(--color-bg-grey-container) !important;
  }
}
`

/**
 * TreeNode component - Renders sidebar menu items
 * Level 1: Parent items with collapse functionality (click anywhere to toggle)
 * Level 2: Child items with active indicator
 */
const TreeNode = ({ className = '', setCollapsed, collapsed, url, title, items, level=0, location, onNavigate, ...rest }) => {
  const isCollapsed = collapsed[url];

  const collapse = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCollapsed(url);
  };

  const hasChildren = items.length !== 0;

  // Get current path from location prop (SSR-safe) or fallback to window.location
  let currentPath = '';
  if (location && location.pathname) {
    currentPath = location.pathname;
  } else if (typeof window !== 'undefined' && window.location) {
    currentPath = window.location.pathname;
  }

  // Normalize paths by removing trailing slashes for comparison (except root '/')
  const normalizePathForComparison = (path) => {
    if (!path || path === '/') return path;
    return path.endsWith('/') ? path.slice(0, -1) : path;
  };

  const normalizedCurrentPath = normalizePathForComparison(currentPath);
  const menuUrl = url || '';
  const normalizedMenuUrl = normalizePathForComparison(menuUrl);
  const prefixedUrl = config.gatsby.pathPrefix + menuUrl;
  const normalizedPrefixedUrl = normalizePathForComparison(prefixedUrl);

  // Active if exact match OR if current path starts with this menu's URL (for parent items)
  const isExactMatch = normalizedCurrentPath === normalizedMenuUrl || normalizedCurrentPath === normalizedPrefixedUrl;
  const isChildPath = normalizedMenuUrl !== '/' && (
    normalizedCurrentPath.startsWith(normalizedMenuUrl + '/') ||
    normalizedCurrentPath.startsWith(normalizedPrefixedUrl + '/')
  );

  // For first-level menu items (level 1): only active on exact match
  // For child items (level 2+): active if exact match
  const active = level === 1
    ? currentPath && isExactMatch
    : currentPath && isExactMatch;

  const calculatedClassName = `${className} item ${active ? 'active' : ''}`;

  const handleLinkClick = () => {
    // Close mobile menu when a link is clicked
    if (onNavigate) {
      onNavigate();
    }
  };

  return (
    <li className={calculatedClassName}>
      {title && (
        <StyledMenuLink to={url} data-level={level} className={active && level === 1 ? 'menu-item-active' : ''} onClick={handleLinkClick}>
          <span>{title}</span>
          {!config.sidebar.frontLine && title && hasChildren ? (
            <CollapseButton
              onClick={collapse}
              aria-label={isCollapsed ? 'Expand menu' : 'Collapse menu'}
              aria-expanded={!isCollapsed}
              style={isCollapsed ? { transform: 'rotate(-180deg)' } : {}}
            >
               <OpenedSvg />
            </CollapseButton>
          ) : null}
        </StyledMenuLink>
      )}

      {!isCollapsed && hasChildren ? (
        <ul>
          {items.map((item, index) => (
            <TreeNode
              key={item.url + index.toString()}
              setCollapsed={setCollapsed}
              collapsed={collapsed}
              level={level + 1}
              location={location}
              onNavigate={onNavigate}
              {...item}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
};



export default TreeNode;
